import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
} from 'recharts';
import { EyeOff } from 'lucide-react';
import styles from './AdminQAReviewStats.module.css';
import { PRISMAX_DATA_PIPELINE_URL } from '../../../configs/config';

const GRANULARITIES = [
    { key: 'day', label: 'D' },
    { key: 'week', label: 'W' },
    { key: 'month', label: 'M' },
];

const GRANULARITY_DESC = {
    day: 'Last 7 days',
    week: 'Last 8 weeks',
    month: 'Last 6 months',
};

const PAGE_SIZE = 15;

const TIER_COLORS = { ex: '#7ab3f0', am: '#f0a04b', in: '#b08bde' };

// ── Helpers ───────────────────────────────────────────────────────────────────

function scoreClass(score) {
    if (score === null || score === undefined) return styles.scorePending;
    if (score >= 85) return styles.scoreGreen;
    if (score >= 75) return styles.scoreAmber;
    return styles.scoreRed;
}

function formatDate(iso) {
    if (!iso) return '—';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '—';
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    const yy = String(d.getFullYear()).slice(-2);
    return `${mm}-${dd}-${yy}`;
}

function tierLabel(tier) {
    return (tier || '').replace(' Member', '') || '—';
}

function tierDotClass(tier) {
    if (tier === 'Explorer Member') return styles.tierEx;
    if (tier === 'Amplifier Member') return styles.tierAm;
    if (tier === 'Innovator Member') return styles.tierIn;
    return '';
}

function getPageNumbers(current, total) {
    const delta = 1;
    const pages = new Set([1, total]);
    for (let i = current - delta; i <= current + delta; i++) {
        if (i > 1 && i < total) pages.add(i);
    }
    const sorted = [...pages].sort((a, b) => a - b);
    const withDots = [];
    let last;
    for (const p of sorted) {
        if (last !== undefined && p - last > 1) withDots.push('...');
        withDots.push(p);
        last = p;
    }
    return withDots;
}

// ── Charts ────────────────────────────────────────────────────────────────────

const ChartTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
        <div style={{
            background: '#1e1e1e', border: '0.5px solid rgba(255,255,255,0.17)',
            borderRadius: 6, padding: '6px 10px', fontSize: 11,
        }}>
            <div style={{ color: 'rgba(255,255,255,0.7)', marginBottom: 4 }}>{label}</div>
            {payload.map((p) => (
                <div key={p.dataKey} style={{ color: p.color }}>
                    {p.name || p.dataKey}: {p.value}
                </div>
            ))}
        </div>
    );
};

const SessionsChart = ({ data }) => (
    <ResponsiveContainer width="100%" height={180}>
        <BarChart data={data} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
            <XAxis dataKey="label" tick={{ fill: 'rgba(255,255,255,0.32)', fontSize: 10 }} axisLine={{ stroke: 'rgba(255,255,255,0.08)' }} tickLine={false} />
            <YAxis tick={{ fill: 'rgba(255,255,255,0.32)', fontSize: 10 }} axisLine={false} tickLine={false} width={36} allowDecimals={false} />
            <Tooltip content={<ChartTooltip />} cursor={{ fill: 'rgba(255,255,255,0.04)' }} />
            <Bar dataKey="sessions" name="Sessions" fill={TIER_COLORS.ex} radius={[4, 4, 0, 0]} isAnimationActive={false} />
        </BarChart>
    </ResponsiveContainer>
);

const ReviewersChart = ({ data }) => (
    <ResponsiveContainer width="100%" height={180}>
        <BarChart data={data} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
            <XAxis dataKey="label" tick={{ fill: 'rgba(255,255,255,0.32)', fontSize: 10 }} axisLine={{ stroke: 'rgba(255,255,255,0.08)' }} tickLine={false} />
            <YAxis tick={{ fill: 'rgba(255,255,255,0.32)', fontSize: 10 }} axisLine={false} tickLine={false} width={36} allowDecimals={false} />
            <Tooltip content={<ChartTooltip />} cursor={{ fill: 'rgba(255,255,255,0.04)' }} />
            <Bar dataKey="ex" name="Explorer" stackId="tier" fill={TIER_COLORS.ex} isAnimationActive={false} />
            <Bar dataKey="am" name="Amplifier" stackId="tier" fill={TIER_COLORS.am} isAnimationActive={false} />
            <Bar dataKey="in" name="Innovator" stackId="tier" fill={TIER_COLORS.in} radius={[4, 4, 0, 0]} isAnimationActive={false} />
        </BarChart>
    </ResponsiveContainer>
);

// ── Episode detail modal ──────────────────────────────────────────────────────

const EpisodeModal = ({ upload, episodes, loading, currentRound, selectedRound, onRoundChange, onClose }) => {
    const [expandedIds, setExpandedIds] = useState(() => new Set());

    useEffect(() => {
        setExpandedIds(new Set());
    }, [episodes]);

    useEffect(() => {
        const handleKey = (e) => { if (e.key === 'Escape') onClose(); };
        window.addEventListener('keydown', handleKey);
        return () => window.removeEventListener('keydown', handleKey);
    }, [onClose]);

    const toggleExpanded = (episodeId) => {
        setExpandedIds((prev) => {
            const next = new Set(prev);
            if (next.has(episodeId)) next.delete(episodeId);
            else next.add(episodeId);
            return next;
        });
    };

    if (!upload) return null;

    const reviewedEpisodes = episodes.filter((ep) => ep.reviewer_count > 0);

    const roundTabs = currentRound > 1
        ? Array.from({ length: currentRound }, (_, i) => i + 1)
        : [];

    return (
        <div className={styles.modalOverlay} onClick={onClose}>
            <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
                <div className={styles.modalHead}>
                    <div className={styles.modalHeadLeft}>
                        <div className={styles.modalTitle}>
                            <span className={styles.upId}>UPL-{upload.upload_id}</span>
                        </div>
                        <div className={styles.modalMeta}>
                            <span>{upload.machine_type}</span>
                            <span className={styles.modalMetaSep}>·</span>
                            <span>{formatDate(upload.uploaded_time)}</span>
                            <span className={styles.modalMetaSep}>·</span>
                            <span>{upload.status}</span>
                            <span className={styles.modalMetaSep}>·</span>
                            <span>{upload.reviewed}/{upload.target ?? '—'} reviews</span>     
                            <span className={styles.modalMetaSep}>·</span>
                            <span>
                                {(loading && episodes.length == 0)
                                ? '- episodes' 
                                : `${episodes.length} episode${episodes.length == 1 ? '' : 's'}`   
                                }                            
                            </span>
                            {upload.final_qa_score != null && (
                                <>
                                    <span className={styles.modalMetaSep}>·</span>
                                    <span>Final QA {upload.final_qa_score}</span>
                                </>
                            )}
                        </div>
                    </div>
                    <button className={styles.modalClose} onClick={onClose} aria-label="Close">×</button>
                </div>
                {roundTabs.length > 0 && (
                    <div className={styles.roundTabs}>
                        {roundTabs.map((r) => (
                            <button
                                key={r}
                                type="button"
                                className={selectedRound === r ? styles.roundTabActive : styles.roundTab}
                                onClick={() => onRoundChange(r)}
                            >
                                Round {r}
                            </button>
                        ))}
                    </div>
                )}
                <div className={styles.modalBody}>
                    {loading && reviewedEpisodes.length === 0 ? (
                        <div className={styles.modalEmpty}>Loading…</div>
                    ) : reviewedEpisodes.length === 0 ? (
                        <div className={styles.modalEmpty}>No episodes reviewed yet.</div>
                    ) : (
                        <div className={loading ? styles.epListLoading : undefined}>
                        {reviewedEpisodes.map((ep) => {
                            const isExpanded = expandedIds.has(ep.episode_id);
                            return (
                                <div key={ep.episode_id} className={styles.epBlock}>
                                    <button
                                        type="button"
                                        className={styles.epBlockHead}
                                        onClick={() => toggleExpanded(ep.episode_id)}
                                        aria-expanded={isExpanded}
                                    >
                                        <div className={styles.epBlockHeadLeft}>
                                            <svg
                                                className={`${styles.epChevron} ${isExpanded ? styles.epChevronOpen : ''}`}
                                                width="9" height="9" viewBox="0 0 9 9" fill="none"
                                            >
                                                <path d="M2 1.5L6 4.5L2 7.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
                                            </svg>
                                            <span className={styles.epName}>{ep.episode_name}</span>
                                            <span className={styles.epCount}> · {ep.reviewer_count > 0 ? `${ep.reviewer_count} reviewer${ep.reviewer_count === 1 ? '' : 's'}` : '0 reviewers'}</span>
                                        </div>
                                        <div className={styles.epAgg}>
                                            <span className={styles.epAggLbl}>Avg</span>
                                            <span className={`${styles.epAggVal} ${scoreClass(ep.avg_score)}`}>{ep.avg_score ?? '—'}</span>
                                        </div>
                                    </button>
                                    {isExpanded && (
                                        <table className={styles.revTable}>
                                            <thead>
                                                <tr>
                                                    <th>User ID</th>
                                                    <th>Tier</th>
                                                    <th>Verdict</th>
                                                    <th className={styles.r}>QA score</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {ep.reviewers.map((r, i) => (
                                                    <tr key={`${ep.episode_id}-${i}`}>
                                                        <td><span className={styles.revUid}>USR-{r.user_id}</span></td>
                                                        <td>
                                                            <span className={`${styles.tierDot} ${tierDotClass(r.tier)}`} />
                                                            {tierLabel(r.tier)}
                                                        </td>
                                                        <td>
                                                            <span className={`${styles.revVerdict} ${r.verdict === 'pass' ? styles.verdictPass : r.verdict === 'fail' ? styles.verdictFail : ''}`}>
                                                                {r.verdict === 'pass' ? 'Pass' : r.verdict === 'fail' ? 'Fail' : '—'}
                                                            </span>
                                                        </td>
                                                        <td className={styles.r}>
                                                            <span className={`${styles.revScore} ${scoreClass(r.score)}`}>{r.score ?? '—'}</span>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    )}
                                </div>
                            );
                        })}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

// ── Main component ────────────────────────────────────────────────────────────

const AdminQAReviewStats = ({ adminToken }) => {
    const [summary, setSummary] = useState(null);
    const [summaryError, setSummaryError] = useState('');

    const [chartStats, setChartStats] = useState(null);
    const [chartLoading, setChartLoading] = useState(false);
    const [sessionsGranularity, setSessionsGranularity] = useState('week');
    const [reviewersGranularity, setReviewersGranularity] = useState('week');

    const [search, setSearch] = useState('');
    const [machineType, setMachineType] = useState('');
    const [round, setRound] = useState('');
    const [status, setStatus] = useState('');
    const [machineTypes, setMachineTypes] = useState([]);
    const [hideTask12, setHideTask12] = useState(false);

    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);

    const [uploads, setUploads] = useState([]);
    const [uploadsCount, setUploadsCount] = useState(0);
    const [uploadsLoading, setUploadsLoading] = useState(false);
    const [uploadsError, setUploadsError] = useState('');

    const [selectedUpload, setSelectedUpload] = useState(null);
    const [episodes, setEpisodes] = useState([]);
    const [episodesLoading, setEpisodesLoading] = useState(false);
    const [episodesCurrentRound, setEpisodesCurrentRound] = useState(1);
    const [selectedRound, setSelectedRound] = useState(1);

    const getHeaders = useCallback(() => ({ Authorization: `Bearer ${adminToken}` }), [adminToken]);

    const buildFilterParams = useCallback((withPage) => {
        const params = new URLSearchParams();
        if (search) params.set('search', search);
        if (machineType) params.set('machine_type', machineType);
        if (round) params.set('round', round);
        if (status) params.set('status', status);
        params.set('hide_task_12', String(hideTask12));
        if (withPage) params.set('page', String(page));
        return params;
    }, [search, machineType, round, status, hideTask12, page]);

    // ── Fetch summary ──
    const fetchSummary = useCallback(async () => {
        setSummaryError('');
        try {
            const params = new URLSearchParams({ hide_task_12: String(hideTask12) });
            const res = await fetch(`${PRISMAX_DATA_PIPELINE_URL}/data/admin/qa-review/summary?${params.toString()}`, { headers: getHeaders() });
            if (!res.ok) throw new Error('Failed to load summary');
            const json = await res.json();
            setSummary(json.data || null);
        } catch (err) {
            console.error(err);
            setSummaryError('Failed to load summary.');
        }
    }, [getHeaders, hideTask12]);

    // ── Fetch chart stats (all granularities in one call) ──
    const fetchChartStats = useCallback(async () => {
        setChartLoading(true);
        try {
            const params = new URLSearchParams({ hide_task_12: String(hideTask12) });
            const res = await fetch(`${PRISMAX_DATA_PIPELINE_URL}/data/admin/qa-review/chart-stats?${params.toString()}`, { headers: getHeaders() });
            if (!res.ok) throw new Error('Failed to load chart stats');
            const json = await res.json();
            setChartStats(json.data || null);
        } catch (err) {
            console.error(err);
        } finally {
            setChartLoading(false);
        }
    }, [getHeaders, hideTask12]);

    // ── Fetch uploads (paginated) ──
    const fetchUploads = useCallback(async () => {
        setUploadsLoading(true);
        setUploadsError('');
        try {
            const params = buildFilterParams(true);
            const res = await fetch(`${PRISMAX_DATA_PIPELINE_URL}/data/admin/qa-review/uploads?${params.toString()}`, { headers: getHeaders() });
            if (!res.ok) throw new Error('Failed to load uploads');
            const json = await res.json();
            setUploads(json.data?.uploads || []);
            setUploadsCount(json.data?.count || 0);
            setTotalPages(json.data?.total_pages || 1);
            setMachineTypes(json.data?.machine_types || []);
        } catch (err) {
            console.error(err);
            setUploadsError('Failed to load uploads.');
        } finally {
            setUploadsLoading(false);
        }
    }, [buildFilterParams, getHeaders]);

    useEffect(() => { fetchSummary(); }, [fetchSummary]);
    useEffect(() => { fetchChartStats(); }, [fetchChartStats]);

    // Reset to page 1 whenever filters change (not when page itself changes).
    useEffect(() => {
        setPage(1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [search, machineType, round, status, hideTask12]);

    useEffect(() => {
        const timeout = setTimeout(() => { fetchUploads(); }, search ? 250 : 0);
        return () => clearTimeout(timeout);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [search, machineType, round, status, hideTask12, page]);

    // ── Episode fetch (round-aware) ──
    const fetchEpisodes = useCallback(async (uploadId, roundParam) => {
        setEpisodesLoading(true);
        try {
            const params = new URLSearchParams({ hide_task_12: String(hideTask12) });
            if (roundParam) params.set('round', String(roundParam));
            const res = await fetch(`${PRISMAX_DATA_PIPELINE_URL}/data/admin/qa-review/uploads/${uploadId}/episodes?${params.toString()}`, { headers: getHeaders() });
            if (!res.ok) throw new Error('Failed to load episodes');
            const json = await res.json();
            setEpisodes(json.data?.episodes || []);
            setEpisodesCurrentRound(json.data?.current_round || 1);
            setSelectedRound(json.data?.round || 1);
        } catch (err) {
            console.error(err);
        } finally {
            setEpisodesLoading(false);
        }
    }, [getHeaders, hideTask12]);

    // ── Row click -> episode modal ──
    const handleRowClick = useCallback((row) => {
        setSelectedUpload(row);
        setEpisodes([]);
        setEpisodesCurrentRound(1);
        setSelectedRound(1);
        fetchEpisodes(row.upload_id);
    }, [fetchEpisodes]);

    const handleRoundChange = useCallback((roundNum) => {
        if (!selectedUpload) return;
        fetchEpisodes(selectedUpload.upload_id, roundNum);
    }, [selectedUpload, fetchEpisodes]);

    // ── Export CSV ──
    const handleExport = useCallback(async () => {
        try {
            const params = buildFilterParams(false);
            const res = await fetch(`${PRISMAX_DATA_PIPELINE_URL}/data/admin/qa-review/uploads/export?${params.toString()}`, { headers: getHeaders() });
            if (!res.ok) throw new Error('Failed to export');
            const blob = await res.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `qa-review-uploads_${new Date().toISOString().slice(0, 10)}.csv`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            console.error(err);
        }
    }, [buildFilterParams, getHeaders]);

    // ── Export reviewers CSV ──
    const handleReviewersExport = useCallback(async () => {
        try {
            const params = new URLSearchParams({ granularity: reviewersGranularity, hide_task_12: String(hideTask12) });
            const res = await fetch(`${PRISMAX_DATA_PIPELINE_URL}/data/admin/qa-review/reviewers/export?${params.toString()}`, { headers: getHeaders() });
            if (!res.ok) throw new Error('Failed to export');
            const blob = await res.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `qa-reviewers_${new Date().toISOString().slice(0, 10)}.csv`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            console.error(err);
        }
    }, [reviewersGranularity, hideTask12, getHeaders]);

    const sessionsData = useMemo(() => {
        const g = chartStats?.[sessionsGranularity];
        if (!g) return [];
        return g.labels.map((label, i) => ({ label, sessions: g.sessions[i] }));
    }, [chartStats, sessionsGranularity]);

    const reviewersData = useMemo(() => {
        const g = chartStats?.[reviewersGranularity];
        if (!g) return [];
        return g.labels.map((label, i) => ({ label, ...g.users[i] }));
    }, [chartStats, reviewersGranularity]);

    const pageNumbers = useMemo(() => getPageNumbers(page, totalPages), [page, totalPages]);

    return (
        <div className={styles.container}>
            <div className={styles.pageToolbar}>
                <button
                    type="button"
                    className={`${styles.utilityButton} ${hideTask12 ? styles.utilityButtonActive : ''}`}
                    onClick={() => {
                        setHideTask12((current) => !current);
                        setSelectedUpload(null);
                    }}
                    aria-pressed={hideTask12}
                    title="Exclude task ID 12 from all QA Review data"
                >
                    <EyeOff size={14} aria-hidden="true" />
                    Hide test data
                </button>
            </div>

            {/* Summary cards */}
            <div className={styles.summaryRow}>
                <div className={styles.sumCard}>
                    <span className={styles.sumLbl}>Total uploads</span>
                    <div className={styles.sumMain}>
                        <span className={styles.sumVal}>{summary ? summary.total.uploads.toLocaleString() : '—'}</span>
                        <span className={styles.sumHrs}>{summary ? `${summary.total.hours.toLocaleString()} hrs` : ''}</span>
                    </div>
                    <span className={styles.sumSub}>Across all machine types</span>
                </div>
                <div className={styles.sumCard}>
                    <span className={styles.sumLbl}>Reviews completed</span>
                    <div className={styles.sumMain}>
                        <span className={`${styles.sumVal} ${styles.green}`}>{summary ? summary.complete.uploads.toLocaleString() : '—'}</span>
                        <span className={styles.sumHrs}>{summary ? `${summary.complete.hours.toLocaleString()} hrs` : ''}</span>
                    </div>
                    <span className={styles.sumSub}>
                        {summary ? `${summary.complete.pct_uploads}% of uploads · ${summary.complete.pct_hours}% of hours` : ''}
                    </span>
                </div>
                <div className={styles.sumCard}>
                    <span className={styles.sumLbl}>Under review</span>
                    <div className={styles.sumMain}>
                        <span className={`${styles.sumVal} ${styles.blue}`}>{summary ? summary.under_review.uploads.toLocaleString() : '—'}</span>
                        <span className={styles.sumHrs}>{summary ? `${summary.under_review.hours.toLocaleString()} hrs` : ''}</span>
                    </div>
                    <span className={styles.sumSub}>
                        {summary ? `${summary.under_review.pct_uploads}% of uploads · ${summary.under_review.pct_hours}% of hours` : ''}
                    </span>
                </div>
                {summaryError && <div className={styles.errorCell}>{summaryError}</div>}
            </div>

            {/* QA session status charts */}
            <div className={styles.section}>
                <div className={styles.sectionHead}>
                    <span className={styles.sectionTitle}>QA session status</span>
                </div>
                <div className={styles.chartGrid}>
                    <div className={styles.chartCard}>
                        <div className={styles.chartHead}>
                            <div className={styles.chartHeadLeft}>
                                <span className={styles.chartTitle}>Sessions reviewed</span>
                                <span className={styles.chartDesc}>{GRANULARITY_DESC[sessionsGranularity]}</span>
                            </div>
                            <div className={styles.seg}>
                                {GRANULARITIES.map((g) => (
                                    <button
                                        key={g.key}
                                        type="button"
                                        className={sessionsGranularity === g.key ? styles.segActive : ''}
                                        onClick={() => setSessionsGranularity(g.key)}
                                    >
                                        {g.label}
                                    </button>
                                ))}
                            </div>
                        </div>
                        {chartLoading ? <div className={styles.chartPlaceholder} /> : <SessionsChart data={sessionsData} />}
                    </div>

                    <div className={styles.chartCard}>
                        <div className={styles.chartHead}>
                            <div className={styles.chartHeadLeft}>
                                <span className={styles.chartTitle}>Reviewers participating</span>
                                <span className={styles.chartDesc}>{GRANULARITY_DESC[reviewersGranularity]}</span>
                            </div>
                            <div className={styles.chartHeadRight}>
                                <button
                                    type="button"
                                    className={styles.chartDownloadBtn}
                                    onClick={handleReviewersExport}
                                    title="Download data"
                                    aria-label="Download data"
                                >
                                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                                        <path d="M6 1v7M3.5 5.5L6 8l2.5-2.5M2 10.5h8" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                </button>
                                <div className={styles.seg}>
                                    {GRANULARITIES.map((g) => (
                                        <button
                                            key={g.key}
                                            type="button"
                                            className={reviewersGranularity === g.key ? styles.segActive : ''}
                                            onClick={() => setReviewersGranularity(g.key)}
                                        >
                                            {g.label}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>
                        <div className={styles.chartLegend}>
                            <div className={styles.legendItem}><span className={styles.legendSwatch} style={{ background: TIER_COLORS.ex }} />Explorer</div>
                            <div className={styles.legendItem}><span className={styles.legendSwatch} style={{ background: TIER_COLORS.am }} />Amplifier</div>
                            <div className={styles.legendItem}><span className={styles.legendSwatch} style={{ background: TIER_COLORS.in }} />Innovator</div>
                        </div>
                        {chartLoading ? <div className={styles.chartPlaceholder} /> : <ReviewersChart data={reviewersData} />}
                    </div>
                </div>
            </div>

            {/* Review status of uploads */}
            <div className={styles.section}>
                <div className={styles.sectionHead}>
                    <span className={styles.sectionTitle}>Review status of uploads</span>
                    <span className={styles.sectionDesc}>{uploadsCount.toLocaleString()} uploads</span>
                </div>
                <div className={styles.tableWrap}>
                    <div className={styles.filterRow}>
                        <div className={styles.searchBox}>
                            <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
                                <circle cx="5.5" cy="5.5" r="4" stroke="rgba(255,255,255,0.32)" strokeWidth="1.2" />
                                <path d="M8.5 8.5L12 12" stroke="rgba(255,255,255,0.32)" strokeWidth="1.2" strokeLinecap="round" />
                            </svg>
                            <input
                                type="text"
                                placeholder="Search upload ID…"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                            />
                        </div>
                        <select className={styles.filterSelect} value={machineType} onChange={(e) => setMachineType(e.target.value)}>
                            <option value="">All machine types</option>
                            {machineTypes.map((m) => <option key={m} value={m}>{m}</option>)}
                        </select>
                        <select className={styles.filterSelect} value={round} onChange={(e) => setRound(e.target.value)}>
                            <option value="">All review rounds</option>
                            <option value="1">Round 1</option>
                            <option value="2">Round 2</option>
                            <option value="3">Round 3</option>
                        </select>
                        <select className={styles.filterSelect} value={status} onChange={(e) => setStatus(e.target.value)}>
                            <option value="">All statuses</option>
                            <option value="complete">Review complete</option>
                            <option value="under_review">Under review</option>
                        </select>
                        <div className={styles.filterSpacer} />
                        <button type="button" className={styles.exportBtn} onClick={handleExport}>
                            <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                                <path d="M6 1v7M3.5 5.5L6 8l2.5-2.5M2 10.5h8" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                            Export CSV
                        </button>
                    </div>
                    <div className={styles.tableScroll}>
                    <table className={styles.uploadsTable}>
                        <thead>
                            <tr>
                                <th>Upload ID</th>
                                <th>Uploaded time</th>
                                <th>Machine type</th>
                                <th>Round</th>
                                <th className={styles.r}>Final QA score</th>
                                <th>Review progress</th>
                                <th>Status</th>
                            </tr>
                        </thead>
                        <tbody className={uploadsLoading ? styles.tbodyLoading : undefined}>
                            {uploads.map((row) => {
                                const target = row.target;
                                const pct = target ? Math.min(100, Math.round((row.reviewed / target) * 100)) : 0;
                                const isComplete = row.status === 'Review complete';
                                return (
                                    <tr key={row.upload_id} onClick={() => handleRowClick(row)}>
                                        <td><span className={styles.upId}>UPL-{row.upload_id}</span></td>
                                        <td>{formatDate(row.uploaded_time)}</td>
                                        <td><span className={styles.mtype}>{row.machine_type}</span></td>
                                        <td><span className={styles.roundTag}>{row.round ? `Round ${row.round}` : '—'}</span></td>
                                        <td className={styles.r}>
                                            <span className={`${styles.score} ${scoreClass(row.final_qa_score)}`}>
                                                {row.final_qa_score ?? '—'}
                                            </span>
                                        </td>
                                        <td>
                                            <div className={styles.progCell}>
                                                <div className={styles.progTrack}>
                                                    <div className={`${styles.progFill} ${isComplete ? styles.progDone : ''}`} style={{ width: `${pct}%` }} />
                                                </div>
                                                <span className={styles.progNum}>{row.reviewed}/{target ?? '—'}</span>
                                            </div>
                                        </td>
                                        <td>
                                            <span className={`${styles.statusTag} ${isComplete ? styles.statusDone : styles.statusReview}`}>
                                                {row.status}
                                            </span>
                                        </td>
                                    </tr>
                                );
                            })}
                            {!uploadsLoading && !uploadsError && uploads.length === 0 && (
                                <tr><td colSpan={7} className={styles.emptyCell}>No uploads match these filters.</td></tr>
                            )}
                            {uploadsError && (
                                <tr><td colSpan={7} className={styles.errorCell}>{uploadsError}</td></tr>
                            )}
                        </tbody>
                    </table>
                    </div>
                    {totalPages > 1 && (
                        <div className={styles.paginationRow}>
                            <span className={styles.paginationLabel}>Page {page} of {totalPages}</span>
                            <div className={styles.paginationBtns}>
                                <button
                                    type="button"
                                    className={styles.pageBtn}
                                    disabled={page <= 1 || uploadsLoading}
                                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                                >
                                    Prev
                                </button>
                                {pageNumbers.map((p, i) => (
                                    p === '...' ? (
                                        <span key={`dots-${i}`} className={styles.pageDots}>…</span>
                                    ) : (
                                        <button
                                            key={p}
                                            type="button"
                                            className={p === page ? styles.pageBtnActive : styles.pageBtn}
                                            disabled={uploadsLoading}
                                            onClick={() => setPage(p)}
                                        >
                                            {p}
                                        </button>
                                    )
                                ))}
                                <button
                                    type="button"
                                    className={styles.pageBtn}
                                    disabled={page >= totalPages || uploadsLoading}
                                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                                >
                                    Next
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {selectedUpload && (
                <EpisodeModal
                    upload={selectedUpload}
                    episodes={episodes}
                    loading={episodesLoading}
                    currentRound={episodesCurrentRound}
                    selectedRound={selectedRound}
                    onRoundChange={handleRoundChange}
                    onClose={() => setSelectedUpload(null)}
                />
            )}
        </div>
    );
};

export default AdminQAReviewStats;
